import { PropsWithChildren, FC, createContext, useMemo } from 'react';

import { useApplication } from '@eneco/common/application';
import { DC_Repositories_Base_Enumerations_BusinessUnit } from '@monorepo-types/dc';
import { Session } from 'next-auth';

import { OpenAPI } from '../client/OpenAPI';
import { Label } from '../client/types';

export type AuthenticationContext = {
  label: Label;
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  customerId: number | null;
  accountId: number | null;
  isAuthenticated: boolean;
};

export const DigitalCoreContext = createContext({} as AuthenticationContext);

type Props = {
  customerContext: {
    customerId?: string;
    accessToken?: string;
    accountId?: number | null;
  };
  label: Label;
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  user?: Session['user'] | null;
  idToken?: Session['idToken'] | null;
};

export const DigitalCoreProvider: FC<PropsWithChildren<Props>> = ({
  children,
  customerContext,
  label,
  businessUnit,
  user,
  idToken,
}) => {
  const { locale } = useApplication();

  const { accountId, customerId: contextCustomerId } = customerContext;
  const customerId = user?.customerId ?? contextCustomerId;

  OpenAPI.TOKEN = idToken ?? undefined;
  OpenAPI.HEADERS['accept-language'] = locale;

  // Memoized to prevent cascading re-renders in all useDC() consumers (infinite loop trigger via useWidgetData)
  const value = useMemo(
    () => ({
      label,
      businessUnit,
      customerId: customerId ? Number(customerId) : null,
      // Sitecore uses -1 as a sentinel for "no active account" (e.g. onboarding); normalize it to null.
      accountId: accountId != null && accountId > -1 ? accountId : null,
      isAuthenticated: Boolean(idToken && customerId),
    }),
    [label, businessUnit, customerId, accountId, idToken],
  );

  return <DigitalCoreContext.Provider value={value}>{children}</DigitalCoreContext.Provider>;
};
